import { useEffect, useState, useRef } from "react";
import Post, { isLifeEventPost } from "./index";
import CreateComment from "./CreateComment";
import Comment from "./Comment";
import { FaTimes } from "react-icons/fa";
import "./style.css";

export default function FullScreenPostViewer({ post, user, onClose, onPostDeleted, isGroupPost = false, groupName, isSavedView = false, onUnsave }) {
  const [comments, setComments] = useState([]);
  // count and setCount are used by CreateComment component
  // eslint-disable-next-line no-unused-vars
  const [count, setCount] = useState(100); // Show all comments in fullscreen
  const createCommentRef = useRef(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle comments change from Post component
  const handleCommentsChange = (newComments, setCommentsFn, newCount, setCountFn) => {
    setComments(newComments);
    setCount(newCount);
  };

  // Handler for comment deletion
  const handleCommentDelete = (deletedCommentId) => {
    if (!deletedCommentId) return;
    setComments((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.filter((c) => (c._id || c.id || c.tempId) !== deletedCommentId);
    });
    setCount((prev) => (prev && prev > 0 ? prev - 1 : 0));
  };

  if (!post) return null;

  const visibleComments = (comments && Array.isArray(comments))
    ? comments.filter(c => !c?.isDeleted)
    : [];

  visibleComments.sort((a, b) => (new Date(b.commentAt || 0) - new Date(a.commentAt || 0)));

  // Only for saved feeling/activity or life event posts: single-column layout with comments list first, then comment input at bottom
  const isFeelingActivityPost = (post.feeling || post.activity) && (!post.images || post.images.length === 0);
  const isLifeEventOnlyPost = isLifeEventPost(post);
  const useSavedSimpleLayout = isSavedView && (isFeelingActivityPost || isLifeEventOnlyPost) && !(post.archived || post.deleted);

  const postOwnerName = post?.user?.name || (post?.user && [post.user.first_name, post.user.last_name].filter(Boolean).join(" ").trim()) || "User";

  const commentsSectionDefault = !(post.archived || post.deleted) && (
    <>
      <div className="post_fullscreen_comments_header">
        <h3>Comments {visibleComments.length > 0 && `(${visibleComments.length})`}</h3>
      </div>
      <div className="post_fullscreen_comments_content">
        <CreateComment
          ref={createCommentRef}
          user={user}
          postId={post._id}
          setComments={setComments}
          setCount={setCount}
        />
        <div className="post_fullscreen_comments_list">
          {visibleComments.length > 0 ? (
            visibleComments.map((comment) => (
              <Comment
                comment={comment}
                key={comment._id || comment.id || comment.tempId}
                postId={post._id}
                setComments={setComments}
                setCount={setCount}
                onDelete={handleCommentDelete}
              />
            ))
          ) : (
            <div className="no_comments_message">
              No comments yet. Be the first to comment!
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Saved feeling/activity or life event: list first, then comment_input_wrap at bottom. No "Comments" subheader.
  const commentsSectionSavedSimple = useSavedSimpleLayout && (
    <>
      <div className="post_fullscreen_comments_content">
        <div className="post_fullscreen_comments_list">
          {visibleComments.length > 0 ? (
            visibleComments.map((comment) => (
              <Comment
                comment={comment}
                key={comment._id || comment.id || comment.tempId}
                postId={post._id}
                setComments={setComments}
                setCount={setCount}
                onDelete={handleCommentDelete}
              />
            ))
          ) : (
            <div className="no_comments_message">
              No comments yet. Be the first to comment!
            </div>
          )}
        </div>
        <CreateComment
          ref={createCommentRef}
          user={user}
          postId={post._id}
          setComments={setComments}
          setCount={setCount}
        />
      </div>
    </>
  );

  return (
    <div className="post_fullscreen_overlay" onClick={onClose}>
      <div
        className={`post_fullscreen_content ${useSavedSimpleLayout ? "post_fullscreen_saved_feeling_activity" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {useSavedSimpleLayout ? (
          <div className="post_fullscreen_feeling_header">
            <h2 className="post_fullscreen_feeling_title">{postOwnerName}&apos;s post</h2>
            <button
              className="post_fullscreen_close"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        ) : (
          <button
            className="post_fullscreen_close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        )}
        <div className={`post_fullscreen_container ${useSavedSimpleLayout ? "post_fullscreen_single_column" : ""}`}>
          {/* Left side - Post content (or full column for saved feeling/activity or life event) */}
          <div className="post_fullscreen_left">
            <div className={`post_fullscreen_wrapper ${isSavedView ? "post_fullscreen_wrapper_saved" : ""}`}>
              <Post 
                post={post} 
                user={user} 
                profile={false}
                gridView={false}
                onPostDeleted={onPostDeleted}
                isGroupPost={isGroupPost}
                groupName={groupName}
                disableFullScreen={true}
                hideComments={true}
                onCommentsChange={handleCommentsChange}
                commentInputFocusRef={createCommentRef}
                isSavedView={isSavedView}
                onUnsave={onUnsave}
              />
            </div>
            {/* Saved feeling/activity or life event: comments below post (post → actions → comments → input) */}
            {useSavedSimpleLayout && (
              <div className="post_fullscreen_single_column_comments">
                {commentsSectionSavedSimple}
              </div>
            )}
          </div>
          
          {/* Right side - Comments (all other posts: default order) */}
          {!useSavedSimpleLayout && (
            <div className="post_fullscreen_right">
              {commentsSectionDefault}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

